/**
 * Unit normalization utilities
 */

const unitMap = {
  // Volume
  'tsp': 'tsp', 'teaspoon': 'tsp', 'teaspoons': 'tsp',
  'tbsp': 'tbsp', 'tablespoon': 'tbsp', 'tablespoons': 'tbsp',
  'cup': 'cup', 'cups': 'cup', 'c': 'cup',
  'fl oz': 'fl oz', 'fluid ounce': 'fl oz', 'fluid ounces': 'fl oz',
  'pt': 'pt', 'pint': 'pt', 'pints': 'pt',
  'qt': 'qt', 'quart': 'qt', 'quarts': 'qt',
  'gal': 'gal', 'gallon': 'gal', 'gallons': 'gal',
  'ml': 'ml', 'milliliter': 'ml', 'milliliters': 'ml',
  'l': 'l', 'liter': 'l', 'liters': 'l',
  // Weight
  'oz': 'oz', 'ounce': 'oz', 'ounces': 'oz',
  'lb': 'lb', 'lbs': 'lb', 'pound': 'lb', 'pounds': 'lb',
  'g': 'g', 'gram': 'g', 'grams': 'g',
  'kg': 'kg', 'kilogram': 'kg', 'kilograms': 'kg',
  // Count
  'piece': 'piece', 'pieces': 'piece',
  'whole': 'whole', 'count': 'count', 'each': 'each',
  // Other
  'clove': 'clove', 'cloves': 'clove',
  'can': 'can', 'cans': 'can',
  'package': 'package', 'packages': 'package',
  'bunch': 'bunch', 'bunches': 'bunch',
};

/**
 * Normalize a unit to its standard form
 */
export function normalizeUnit(unit) {
  if (!unit) return '';
  const normalized = unit.toLowerCase().trim();
  return unitMap[normalized] || normalized;
}

/**
 * Parse a quantity string (e.g., "1/2", "1.5", "2 1/4", "½", "1½")
 */
export function parseQuantity(quantityStr) {
  if (typeof quantityStr === 'number') return quantityStr;
  if (!quantityStr) return 0;

  let str = quantityStr.toString().trim();

  // Convert unicode fractions to ASCII
  const unicodeFractionMap = {
    '¼': '1/4', '½': '1/2', '¾': '3/4',
    '⅓': '1/3', '⅔': '2/3',
    '⅕': '1/5', '⅖': '2/5', '⅗': '3/5', '⅘': '4/5',
    '⅙': '1/6', '⅚': '5/6', '⅐': '1/7',
    '⅛': '1/8', '⅜': '3/8', '⅝': '5/8', '⅞': '7/8',
    '⅑': '1/9', '⅒': '1/10',
  };

  for (const [unicode, ascii] of Object.entries(unicodeFractionMap)) {
    str = str.replace(unicode, ' ' + ascii);
  }
  str = str.trim();

  // Mixed number: "1 1/2"
  const mixedMatch = str.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixedMatch) {
    return parseInt(mixedMatch[1]) + parseInt(mixedMatch[2]) / parseInt(mixedMatch[3]);
  }

  // Simple fraction: "1/2"
  const fractionMatch = str.match(/^(\d+)\/(\d+)$/);
  if (fractionMatch) {
    return parseInt(fractionMatch[1]) / parseInt(fractionMatch[2]);
  }

  return parseFloat(str) || 0;
}

/**
 * Format a quantity for display (round to practical amounts)
 * FIX: Previous regex /\.?0+$/ incorrectly stripped trailing zeros
 * from whole numbers (10 → "1", 20 → "2"). Now only strips after decimal point.
 */
export function formatQuantity(quantity) {
  if (!quantity && quantity !== 0) return '';

  const num = typeof quantity === 'number' ? quantity : parseQuantity(quantity);
  const rounded = Math.round(num * 100) / 100;

  // Convert to string and only strip trailing zeros after a decimal point
  const str = rounded.toString();
  if (str.includes('.')) {
    return str.replace(/0+$/, '').replace(/\.$/, '');
  }
  return str;
}

/**
 * Get category color for styling
 */
export function getCategoryColor(category) {
  const colors = {
    'produce': 'bg-green-100 text-green-800',
    'protein': 'bg-red-100 text-red-800',
    'dairy': 'bg-blue-100 text-blue-800',
    'pantry': 'bg-yellow-100 text-yellow-800',
    'frozen': 'bg-cyan-100 text-cyan-800',
    'bakery': 'bg-orange-100 text-orange-800',
    'other': 'bg-gray-100 text-gray-800',
  };
  return colors[category.toLowerCase()] || colors['other'];
}

/**
 * Get categories list
 */
export function getCategories() {
  return ['produce', 'protein', 'dairy', 'pantry', 'frozen', 'bakery', 'other'];
}

/**
 * Normalize item name (lowercase, trim)
 */
export function normalizeItemName(item) {
  return item.toLowerCase().trim();
}
